import { ApiOrder } from 'api/types';
import { determineStopType, isTPSLOrder } from 'components/orders/orderUtil';
import { formatNumber } from 'lib/display/formatting';

import { ChartProps } from '../chartTypes';
import {
  IChartWidgetApi,
  IOrderLineAdapter,
} from '../external/charting_library';

import { NEGATIVE_COLOR, POSITIVE_COLOR } from './drawingColors';

/** Open Orders rendered on chart */
let renderedOrderLines: { orderId: string; lineAdapter: IOrderLineAdapter }[] =
  [];

const getPrice = (order: ApiOrder) => Number(order.triggerPrice || order.price);

const NO_FN = () => {
  // Empty
};

// TODO: error on empty chart
export function renderOrders(
  chart: IChartWidgetApi | undefined,
  {
    ordersList,
    /** Per market */
    maximumPositionSize,
    stepSizes,
    tickSizes,
    isMobile,
    onCancelOrder,
  }: Pick<
    ChartProps,
    | 'ordersList'
    | 'maximumPositionSize'
    | 'stepSizes'
    | 'tickSizes'
    | 'isMobile'
    | 'onCancelOrder'
  >
) {
  if (!chart) {
    return;
  }

  const filteredOrderList = [...(ordersList || [])].filter(
    order => !((!order.triggerPrice && !order.price) || order.type === 'market')
  );

  const removeLines = renderedOrderLines.slice(filteredOrderList.length || 0);
  /** We have to reuse lines cause .remove() causing memory leak inside Chart */
  const reuseLines = renderedOrderLines.slice(0, filteredOrderList.length || 0);

  removeLines.forEach(({ lineAdapter }) => {
    console.debug('[chart_orders] REMOVING', removeLines.length);
    lineAdapter?.remove();
  });

  renderedOrderLines = filteredOrderList.reverse().map((order, index) => {
    if (!reuseLines[index]) {
      console.debug(
        '[chart_orders] ADDING NEW',
        filteredOrderList.length - reuseLines.length
      );
      const orderLine = chart.createOrderLine();
      reuseLines.push({
        orderId: order.orderId,
        lineAdapter: orderLine,
      });
    }
    const updatingLine = reuseLines[index];
    updatingLine.orderId = order.orderId;
    const lineAdapter = updatingLine.lineAdapter;

    const tpSLClosure = isTPSLOrder(order, maximumPositionSize);
    const orderColor = order.side === 'buy' ? POSITIVE_COLOR : NEGATIVE_COLOR;
    const stopType = determineStopType(order.type);

    lineAdapter
      .setBodyBackgroundColor('#000')
      .setLineColor(orderColor)
      .setBodyBorderColor(orderColor)
      .setQuantityBackgroundColor(orderColor)
      .setQuantityBorderColor(orderColor)
      .setBodyTextColor('white')
      .setText(
        order.triggerPrice
          ? `${
              stopType === 'takeProfit'
                ? 'TP'
                : stopType === 'stopLoss'
                ? 'SL'
                : '?'
            } ${
              isMobile
                ? ''
                : formatNumber(order.triggerPrice, {
                    fixedDecimals: tickSizes[order.market]?.visibleDecimals,
                  })
            }`
          : order.price
          ? `Limit ${
              isMobile
                ? ''
                : formatNumber(order.price, {
                    fixedDecimals: tickSizes[order.market]?.visibleDecimals,
                  })
            }`
          : '--'
      )
      .setPrice(getPrice(order))
      .setQuantity(
        tpSLClosure
          ? ''
          : `${formatNumber(order.originalQuantity, {
              fixedDecimals: stepSizes[order.market]?.visibleDecimals,
            })} ${order.baseSymbol}`
      )
      // Move it more to the left, 85/100%
      .setExtendLeft(false)
      .setLineStyle(0)
      .setLineLength(isMobile ? -8 : -200, 'pixel')
      // Reset handlers
      .onMove(NO_FN)
      .onCancel(NO_FN);

    if (!isMobile) {
      // Add cancellation (Desktop only)
      lineAdapter
        .setCancellable(true)
        .setCancelButtonBackgroundColor('#000')
        .setCancelButtonIconColor('#FFF')
        .setCancelButtonBorderColor(orderColor)
        .onCancel(() => {
          onCancelOrder(order.orderId);
        });
    }
    return updatingLine;
  });
}

export function resetOrderPrice(order: ApiOrder) {
  const orderLine = renderedOrderLines?.find(
    orderLine => orderLine.orderId === order.orderId
  );
  orderLine?.lineAdapter.setPrice(getPrice(order));
}

export function updateOrdersHandlers(
  chart: IChartWidgetApi | undefined,
  {
    ordersList,
    /** Per market */
    maximumPositionSize,
    isMobile,
    position,
    onChangeOrderPrice,
  }: Pick<
    ChartProps,
    | 'ordersList'
    | 'maximumPositionSize'
    | 'isMobile'
    | 'position'
    | 'onChangeOrderPrice'
  >
) {
  ordersList?.forEach(order => {
    if (
      !chart ||
      (!order.triggerPrice && !order.price) ||
      order.type === 'market'
    ) {
      return;
    }
    const tpSLClosure = isTPSLOrder(order, maximumPositionSize);
    // const orderColor = order.side === 'buy' ? POSITIVE_COLOR : NEGATIVE_COLOR;
    if (!isMobile) {
      const orderLine = renderedOrderLines.find(
        line => line.orderId === order.orderId
      )?.lineAdapter;
      // Add re-pricing (Desktop only)
      if (
        // For TP/SL there must be position and triggerType
        (tpSLClosure && order.triggerType && position) ||
        // Otherwise all orders params allowed
        !tpSLClosure
      ) {
        orderLine?.onMove(() => {
          const updatedPrice = orderLine.getPrice();
          onChangeOrderPrice(order, updatedPrice);
        });
      }
    }
  });
}
